/**
 * PixelArtEditor — native settings UI for the Pixel Quest theme.
 * Teachers configure behaviour here; slide content is always edited
 * in the standard capsule editor.
 */

import {type ReactNode} from 'react'
import {
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  View,
} from 'react-native'

import {type ThemeEditorProps} from '#/types/capsule'

const G = '#00ff41'
const GOLD = '#ffd700'
const BG = '#080c14'
const PANEL = '#0b1220'
const PANEL2 = '#0d1a2e'
const TEXT = '#c8ffc8'
const TEXT2 = 'rgba(200,255,200,0.55)'
const TEXT3 = 'rgba(200,255,200,0.25)'
const FONT = Platform.OS === 'ios' ? 'Courier New' : 'monospace'

function PixelText({children, style}: {children: ReactNode; style?: object}) {
  return (
    <Text style={[{fontFamily: FONT, color: TEXT}, style]}>{children}</Text>
  )
}

function SettingRow({
  label,
  hint,
  value,
  onToggle,
}: {
  label: string
  hint: string
  value: boolean
  onToggle: () => void
}) {
  return (
    <View style={s.row}>
      <View style={s.rowLeft}>
        <PixelText style={s.rowLabel}>{label}</PixelText>
        <PixelText style={s.rowHint}>{hint}</PixelText>
      </View>
      <Switch
        value={value}
        onValueChange={onToggle}
        trackColor={{false: PANEL2, true: 'rgba(0,255,65,0.4)'}}
        thumbColor={value ? G : TEXT3}
        ios_backgroundColor={PANEL2}
      />
    </View>
  )
}

export function PixelArtEditor({capsule, onSettingsUpdate}: ThemeEditorProps) {
  const settings = capsule.behaviorSettings ?? {}

  const instantFeedback = settings.instantFeedback !== false
  const showProgressBar = settings.showProgressBar !== false
  const shuffleOptions = Boolean(settings.shuffleOptions)

  function toggle(key: string, current: boolean) {
    onSettingsUpdate({...settings, [key]: !current})
  }

  return (
    <ScrollView style={s.root} contentContainerStyle={s.content}>
      {/* Header */}
      <View style={s.header}>
        <View style={s.pixelBorder}>
          <PixelText style={s.themeIcon}>👾</PixelText>
          <PixelText style={s.themeName}>PIXEL QUEST</PixelText>
          <PixelText style={s.themeTag}>HTML BRIDGE THEME v1.0</PixelText>
        </View>
      </View>

      {/* Description */}
      <View style={s.descBox}>
        <PixelText style={s.descText}>
          {'A retro 8-bit RPG learning adventure.\n'}
          {'Students earn XP, see animated feedback,\n'}
          {'and unlock QUEST COMPLETE screens.\n\n'}
          {'★ Supports all 20 slide types\n'}
          {'★ Custom pixel art result screen\n'}
          {'★ Press Start 2P font\n'}
          {'★ Web only (mobile coming soon)'}
        </PixelText>
      </View>

      {/* Settings */}
      <PixelText style={s.sectionTitle}>{'// GAME CONFIG'}</PixelText>

      <View style={s.settingsBox}>
        <SettingRow
          label="INSTANT FEEDBACK"
          hint="Show ✓ / ✗ immediately after each answer"
          value={instantFeedback}
          onToggle={() => toggle('instantFeedback', instantFeedback)}
        />
        <View style={s.divider} />
        <SettingRow
          label="HP BAR"
          hint="Show progress as a pixel HP bar in the HUD"
          value={showProgressBar}
          onToggle={() => toggle('showProgressBar', showProgressBar)}
        />
        <View style={s.divider} />
        <SettingRow
          label="SHUFFLE OPTIONS"
          hint="Randomise choice order on each play"
          value={shuffleOptions}
          onToggle={() => toggle('shuffleOptions', shuffleOptions)}
        />
      </View>

      {/* Preview note */}
      <View style={s.noteBox}>
        <PixelText style={s.noteText}>
          {'▶ Press PLAY to preview how your capsule\n'}
          {'  looks in the Pixel Quest theme.'}
        </PixelText>
      </View>
    </ScrollView>
  )
}

const s = StyleSheet.create({
  root: {flex: 1, backgroundColor: BG},
  content: {padding: 16, gap: 14},

  header: {alignItems: 'center'},
  pixelBorder: {
    alignItems: 'center',
    borderWidth: 3,
    borderColor: G,
    padding: 16,
    shadowColor: G,
    shadowOpacity: 0.5,
    shadowRadius: 12,
    shadowOffset: {width: 0, height: 0},
    elevation: 8,
    backgroundColor: PANEL,
    gap: 6,
  },
  themeIcon: {fontSize: 32},
  themeName: {
    fontSize: 13,
    color: G,
    letterSpacing: 3,
    fontFamily: FONT,
    textShadowColor: G,
    textShadowRadius: 8,
    textShadowOffset: {width: 0, height: 0},
  },
  themeTag: {fontSize: 6, color: TEXT2, fontFamily: FONT},

  descBox: {
    backgroundColor: PANEL,
    borderWidth: 2,
    borderColor: TEXT3,
    padding: 14,
  },
  descText: {fontSize: 8, color: TEXT2, fontFamily: FONT, lineHeight: 18},

  sectionTitle: {fontSize: 8, color: G, fontFamily: FONT, letterSpacing: 2},

  settingsBox: {
    backgroundColor: PANEL,
    borderWidth: 3,
    borderColor: G,
    shadowColor: G,
    shadowOpacity: 0.3,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 0},
    elevation: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    gap: 12,
  },
  rowLeft: {flex: 1, gap: 4},
  rowLabel: {fontSize: 8, color: TEXT, fontFamily: FONT, letterSpacing: 1},
  rowHint: {fontSize: 6, color: TEXT2, fontFamily: FONT, lineHeight: 14},
  divider: {height: 1, backgroundColor: TEXT3, marginHorizontal: 14},

  noteBox: {
    backgroundColor: 'rgba(255,215,0,0.06)',
    borderLeftWidth: 3,
    borderLeftColor: GOLD,
    padding: 12,
  },
  noteText: {fontSize: 7, color: GOLD, fontFamily: FONT, lineHeight: 18},
})
